import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  APP_PATH,
  DISABLE_SCANNER_FOR_ROUTES,
  LocalStorageKeys,
  USER_ACTIVITY_EXPIRED_TIME,
} from "@is/constants";
import { UserActivityContext } from "@is/contexts/UserActivityContext";
import { useStoreScanner } from "@is/hooks/scanner";
import { LocalStorageService } from "@is/services";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import { ScannerDetails } from "@is/types/scanner";
import { NOT_BARCODE_SYMBOLS } from "@is/utils/regex";

export type ScannerContextValue = {
  scannerName: string;
  scannedValue: string;
  scannedTime: number;
  handleSelectScanner: () => void;
  handleDisconnectScanner: () => void;
  devices: any;
  updateDeviceList: () => void;
  resetScannedValue: () => void;
  setScannerDisabled: (value: boolean) => void;
} | null;

export const ScannerContext = React.createContext<ScannerContextValue>(null);

export const ScannerProvider = ({ children }: PropsWithChildren) => {
  const [scanner, setScanner] = useState(null);
  const [devices, setDevices] = useState(null);
  const [scannerDisabled, setScannerDisabled] = useState(false);
  const [scannerName, setScannerName] = useState("");
  const [scannedValue, setScannedValue] = useState("");
  const [scannedTime, setScannedTime] = useState(0);

  const { setUserActivityModalExpiredTime, setShowUserActivityModal } =
    useContext(UserActivityContext);

  const [, saveScanner] = useStoreScanner();

  const updateDeviceList = useCallback(async () => {
    const deviceList = await navigator["hid"].getDevices();

    setDevices(deviceList);
  }, []);

  const handleSelectScanner = useCallback(async () => {
    const [scanner] = await navigator["hid"].requestDevice({
      filters: [],
    });

    if (scanner) {
      console.info("Selected scanner details: ", scanner);
      const { vendorId, productId } = scanner;

      if (!scanner.opened) await scanner.open();

      /* Honeywell devices need to be put into HID mode */
      if (scanner.vendorId === 0x0c2e) {
        console.info("put device into HID mod");
        await scanner.sendFeatureReport(254, new Uint8Array([0x01]));

        trackEvent({
          eventType: TrackingEventTypes.ScannerInHIDMode,
          data: {
            scannerName: scanner.productName,
          },
        });
      }

      const scannerDetails: ScannerDetails = {
        vendorId,
        productId,
      };

      LocalStorageService.setItem(
        LocalStorageKeys.ScannerDetails,
        scannerDetails
      );

      setScanner(scanner);

      saveScanner({
        scannerName: scanner.productName,
        scannerVendorId: `${vendorId}`,
        scannerProductId: `${productId}`,
      });

      trackEvent({
        eventType: TrackingEventTypes.ScannerConnected,
        data: {
          scannerName: scanner.productName,
        },
      });
    }
  }, [saveScanner]);

  const resetScannedValue = useCallback(() => {
    setScannedValue("");
    setScannedTime(0);
  }, []);

  const handleDisconnectScanner = useCallback(() => {
    setScanner(null);
    setScannerName("");
    resetScannedValue();

    LocalStorageService.removeItem(LocalStorageKeys.ScannerDetails);

    trackEvent({
      eventType: TrackingEventTypes.ScannerDisconnected,
      data: {},
    });
  }, [resetScannedValue]);

  const contextValue: ScannerContextValue = useMemo(
    () => ({
      scannerName,
      scannedValue,
      scannedTime,
      handleSelectScanner,
      handleDisconnectScanner,
      devices,
      updateDeviceList,
      resetScannedValue,
      setScannerDisabled,
    }),
    [
      devices,
      handleSelectScanner,
      handleDisconnectScanner,
      scannedValue,
      scannedTime,
      scannerName,
      updateDeviceList,
      resetScannedValue,
      setScannerDisabled,
    ]
  );

  const validRoute = useMemo(() => {
    const pathname = window.location.pathname;
    const route = pathname.substring(APP_PATH.length, pathname.length);

    return !DISABLE_SCANNER_FOR_ROUTES.includes(route);
  }, []);

  const handleInputReport = useCallback(
    ({ data }) => {
      if (validRoute) {
        let resultBuffer = "";
        const length = data.getUint8(0);

        for (let i = 0; i < length; i++) {
          const character = data.getUint8(i + 4);

          if (character !== 0x0d) {
            resultBuffer += String.fromCharCode(data.getUint8(i + 4));
          }
        }
        const ean = resultBuffer.toString();
        console.log("Scanned EAN: ", ean);

        const trimmedEan = ean.replace(NOT_BARCODE_SYMBOLS, "");
        console.log("Trimmed EAN", trimmedEan);

        setScannedValue(trimmedEan);
        setScannedTime(Date.now());

        const expiredTime = Date.now() + USER_ACTIVITY_EXPIRED_TIME;
        setUserActivityModalExpiredTime(expiredTime);
        setShowUserActivityModal(false);
      }
    },
    [setShowUserActivityModal, setUserActivityModalExpiredTime, validRoute]
  );

  useEffect(() => {
    if (scanner && !scannerDisabled) {
      scanner.addEventListener("inputreport", handleInputReport);

      setScannerName(scanner.productName);
    } else {
      scanner?.removeEventListener("inputreport", handleInputReport);
    }
  }, [handleInputReport, scanner, scannerDisabled]);

  useEffect(() => {
    async function connectScanner() {
      if (!scanner && validRoute) {
        const scannerDetails: ScannerDetails = JSON.parse(
          LocalStorageService.getItem(LocalStorageKeys.ScannerDetails)
        );

        const devices = await navigator["hid"].getDevices();
        const selectedScanner = devices.find(
          (device) =>
            device.vendorId === scannerDetails?.vendorId &&
            device.productId === scannerDetails?.productId
        );

        if (!selectedScanner?.opened) await selectedScanner?.open();
        /* Honeywell devices need to be put into HID mode */
        if (scanner?.vendorId == 0x0c2e) {
          await scanner.sendFeatureReport(254, new Uint8Array([0x01]));
        }

        setScanner(selectedScanner);
      }
    }

    connectScanner();
  }, []);

  return (
    <ScannerContext.Provider value={contextValue}>
      {children}
    </ScannerContext.Provider>
  );
};
